import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AlertOctagon, Plus, Clock } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

const SEV = ["mild", "moderate", "severe", "life_threatening", "fatal"];
const REL = ["not_related", "unlikely", "possible", "probable", "definite"];
const OUT = ["recovered", "recovering", "not_recovered", "sequelae", "fatal", "unknown"];
const STATUSES = ["draft", "reported_to_sponsor", "regulator_notified", "followed_up", "closed"];

export default function AdverseEvents() {
  const [aes, setAes] = useState([]);
  const [studies, setStudies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    study_id: "", subject_id: "", event_date: new Date().toISOString().slice(0, 16),
    description: "", severity: "moderate", relatedness: "possible", outcome: "recovering",
    is_sae: false, is_susar: false, is_death: false, device_related: false,
    action_taken: "", concomitant_meds: "",
  });
  const [statusEdit, setStatusEdit] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "draft", notes: "", reported_to: "" });

  const load = useCallback(() => {
    api.get("/clinical/adverse-events").then(({ data }) => setAes(data))
      .catch((e) => logger.warn("AE load:", e?.response?.status));
    api.get("/studies").then(({ data }) => setStudies(data))
      .catch((e) => logger.warn("Studies load:", e?.response?.status));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = { ...form, event_date: new Date(form.event_date).toISOString() };
      await api.post("/clinical/adverse-events", payload);
      toast.success("Adverse event logged.");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const openStatus = (ae) => {
    setStatusEdit(ae);
    setStatusForm({ status: ae.status, notes: "", reported_to: "" });
  };
  const saveStatus = async () => {
    try {
      await api.patch(`/clinical/adverse-events/${statusEdit.ae_id}`, statusForm);
      toast.success("Status updated.");
      setStatusEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="vq-overline mb-2 flex items-center gap-2"><AlertOctagon className="w-3 h-3" /> Clinical · Adverse Events</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">AE / SAE Reporting</h1>
            <p className="text-slate-600 mt-2 max-w-3xl">
              ISO 14155 §7.7 · FDA 21 CFR 803 (MDR) · EU MDR Art. 87 vigilance timers. Windows shown are indicative;
              a qualified investigator / sponsor must confirm the applicable rule.
            </p>
          </div>
          <Button data-testid="new-ae-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> Log event</Button>
        </div>

        <div className="vq-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Event date</th>
                <th className="text-left px-3 py-2">Study / Subject</th>
                <th className="text-left px-3 py-2">Severity</th>
                <th className="text-left px-3 py-2">Related</th>
                <th className="text-left px-3 py-2">Flags</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2"><Clock className="w-3 h-3 inline" /> Windows</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody data-testid="ae-tbody">
              {aes.length === 0 && (
                <tr><td colSpan="8" className="text-center px-3 py-6 text-slate-400">No adverse events logged yet.</td></tr>
              )}
              {aes.map((a) => (
                <tr key={a.ae_id} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-xs">{a.event_date?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-xs">{a.study_id}{a.subject_id ? ` · ${a.subject_id}` : ""}</td>
                  <td className="px-3 py-2 text-xs">
                    <Badge variant={a.severity === "fatal" || a.severity === "life_threatening" ? "destructive" : "secondary"}>{a.severity}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">{a.relatedness.replace("_", " ")}</td>
                  <td className="px-3 py-2 text-xs">
                    {a.is_sae && <Badge variant="destructive" className="mr-1">SAE</Badge>}
                    {a.is_susar && <Badge variant="destructive" className="mr-1">SUSAR</Badge>}
                    {a.is_death && <Badge variant="destructive" className="mr-1">Death</Badge>}
                    {a.device_related && <Badge variant="secondary" className="mr-1">Device</Badge>}
                  </td>
                  <td className="px-3 py-2 text-xs">{a.status.replace("_", " ")}</td>
                  <td className="px-3 py-2 text-xs">
                    {a.overdue_windows?.length > 0 ? (
                      <span className="text-red-600 font-semibold">Overdue: {a.overdue_windows.join(", ")}</span>
                    ) : Object.keys(a.deadlines || {}).length ? (
                      <span className="text-slate-500">{Object.keys(a.deadlines).join(", ")}</span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" data-testid={`ae-status-${a.ae_id}`} onClick={() => openStatus(a)}>Update</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Log adverse event</DialogTitle>
              <DialogDescription>ISO 14155 / MDR / FDA 21 CFR 803-aligned capture. Windows compute automatically.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Study</Label>
                <Select value={form.study_id} onValueChange={(v) => setForm({ ...form, study_id: v })}>
                  <SelectTrigger data-testid="ae-study"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{studies.map((s) => <SelectItem key={s.study_id} value={s.study_id}>{s.title || s.study_id}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject ID (optional)</Label>
                <Input data-testid="ae-subject" value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Event date/time</Label>
                <Input data-testid="ae-date" type="datetime-local" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea data-testid="ae-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="ae-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEV.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Relatedness</Label>
                <Select value={form.relatedness} onValueChange={(v) => setForm({ ...form, relatedness: v })}>
                  <SelectTrigger data-testid="ae-related"><SelectValue /></SelectTrigger>
                  <SelectContent>{REL.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Outcome</Label>
                <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                  <SelectTrigger data-testid="ae-outcome"><SelectValue /></SelectTrigger>
                  <SelectContent>{OUT.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 grid grid-cols-4 gap-2 text-xs">
                <label className="flex items-center gap-2"><input data-testid="ae-sae" type="checkbox" checked={form.is_sae} onChange={(e) => setForm({ ...form, is_sae: e.target.checked })} /> SAE</label>
                <label className="flex items-center gap-2"><input data-testid="ae-susar" type="checkbox" checked={form.is_susar} onChange={(e) => setForm({ ...form, is_susar: e.target.checked })} /> SUSAR</label>
                <label className="flex items-center gap-2"><input data-testid="ae-death" type="checkbox" checked={form.is_death} onChange={(e) => setForm({ ...form, is_death: e.target.checked })} /> Death</label>
                <label className="flex items-center gap-2"><input data-testid="ae-device" type="checkbox" checked={form.device_related} onChange={(e) => setForm({ ...form, device_related: e.target.checked })} /> Device-related</label>
              </div>
              <div className="col-span-2">
                <Label>Action taken (optional)</Label>
                <Textarea data-testid="ae-action" rows={2} value={form.action_taken} onChange={(e) => setForm({ ...form, action_taken: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="ae-submit" onClick={submit} disabled={!form.study_id || form.description.length < 10}>Log event</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!statusEdit} onOpenChange={(o) => !o && setStatusEdit(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update AE status — {statusEdit?.ae_id}</DialogTitle>
              <DialogDescription>Advance the vigilance workflow. Timestamps are recorded automatically.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Status</Label>
                <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                  <SelectTrigger data-testid="ae-new-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reported to (optional)</Label>
                <Input data-testid="ae-reported-to" value={statusForm.reported_to} onChange={(e) => setStatusForm({ ...statusForm, reported_to: e.target.value })} placeholder="FDA MDR / EU EudaMed / …" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea data-testid="ae-status-notes" rows={3} value={statusForm.notes} onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStatusEdit(null)}>Cancel</Button>
              <Button data-testid="ae-save-status" onClick={saveStatus}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
